import { STRINGS } from '@/strings/strings';
import type { OrderableSalesUnit } from '../../types';

/**
 * Block 6 · 6.2 · Three-tile Buchart selector (Sebastian ruling
 * 2026-09-09). Retires the pre-Block-6 Q1 Buchart / Q2 Format
 * two-question flow; the customer picks one of three tiles
 * directly.
 *
 * Order in the UI: Hardcover → Layflat → Pappbuch. Prices +
 * descriptions rendered VERBATIM from STRINGS. All three tiles
 * derive their enabled/disabled state from the backend
 * `orderableSalesUnits` envelope: a productClass with an entry in
 * that array is enabled and carries the entry's slug; a
 * productClass without an entry is disabled and has no slug wired
 * (a customer picking a disabled tile is a no-op). Sebastian
 * ruling 2026-09-13: the tile must not carry its own truth —
 * that is the defect class we keep hitting (pre-2026-09-13 the
 * Pappbuch tile hardcoded `disabled: true` and `slug: null` on
 * top of the backend `available: false`, so a backend flip alone
 * left the tile ungreyable).
 *
 * Each tile has a slot for a small side-view illustration
 * (hardcover with curved pages, layflat flat, pappbuch thick
 * boards). WebP set lives at `/pwa/public/{hardcover,layflat,
 * pappbuch}.webp`, resized to 720 px wide (the tile renders in a
 * ≤345 px column on desktop / ≤375 px on mobile, aspect-video +
 * bg-contain) at quality 82. Total payload ≈ 70 KB across the
 * three tiles. Missing files render nothing (empty slot) per the
 * same fail-silent CSS pattern as the side panels — the
 * `background-image` CSS silently paints nothing on a 404, no
 * broken-image icon.
 *
 * ═══ SLUG WIRING ═══════════════════════════════════════════════════
 *
 * Each clickable tile maps to a SLUG in `envelope.orderableSalesUnits`.
 * With single-format-per-binding at launch (one 21×21 unit per
 * productClass), the mapping is: Hardcover tile → the sole
 * `productClass === 'hardcover'` unit's slug; Layflat tile → the
 * sole `productClass === 'layflat'` unit's slug. If a productClass
 * is not present in orderableSalesUnits (backend catalog change,
 * flag off), the corresponding tile is disabled — the customer
 * cannot click through to a review screen with a missing slug.
 */

const TILE_IMAGE_SLOT_CLASS =
  'aspect-video w-full rounded-md bg-dream-navy/5 bg-no-repeat bg-contain bg-center';

const TILE_BASE_CLASS =
  'flex flex-col gap-3 rounded-lg border border-book-border bg-book-card p-4 text-left text-dream-navy transition-colors';
const TILE_ENABLED_CLASS = 'hover:border-dream-navy hover:bg-dream-navy/5';
const TILE_SELECTED_CLASS = 'ring-2 ring-dream-gold border-dream-navy';
const TILE_DISABLED_CLASS = 'opacity-50 cursor-not-allowed';

export interface BuchartTilesProps {
  readonly orderableSalesUnits: readonly OrderableSalesUnit[];
  readonly selectedSlug: string | null;
  readonly onPick: (slug: string) => void;
  /**
   * Sebastian ruling 2026-09-25 (Webflow shipping-price update).
   * Shipping charge in integer cents, taken from the envelope
   * (`envelope.shippingCents`, sourced from
   * `BOOK_CONFIG.shipping_de_cents`). Rendered as small-type text
   * under each tile price via the `tileShippingHint` template —
   * "inkl. MwSt., zzgl. 7,50 € Versand" today. A future config
   * change updates the envelope value and the tile line follows,
   * rather than shipping a hardcoded string that can go stale.
   */
  readonly shippingCents: number;
}

/**
 * Format integer cents as a German-locale price string:
 * 750 → "7,50 €". Local helper — same shape as the one in
 * BestellungReviewScreen but kept here so BuchartTiles is
 * self-contained. If a third consumer appears the helper lifts
 * to `pwa/src/features/book/formatEuros.ts`.
 */
function formatEuros(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

interface TileSpec {
  readonly key: 'hardcover' | 'layflat' | 'pappbuch';
  readonly title: string;
  readonly price: string;
  readonly description: string;
  readonly imageUrl: string;
  readonly disabled: boolean;
}

export function BuchartTiles({
  orderableSalesUnits,
  selectedSlug,
  onPick,
  shippingCents,
}: BuchartTilesProps) {
  // Substituted into `tileShippingHint` — one place per render.
  const shippingHint = STRINGS.book.bestellung.tileShippingHint.replace(
    '{shipping}',
    formatEuros(shippingCents),
  );
  // Slug lookup: find the sole orderable unit for each productClass.
  // Returns undefined when the class is not orderable.
  const hardcoverUnit = orderableSalesUnits.find((u) => u.productClass === 'hardcover');
  const layflatUnit = orderableSalesUnits.find((u) => u.productClass === 'layflat');
  const pappbuchUnit = orderableSalesUnits.find((u) => u.productClass === 'pappbuch');

  const tiles: readonly TileSpec[] = [
    {
      key: 'hardcover',
      title: STRINGS.book.bestellung.tileHardcoverTitle,
      price: STRINGS.book.bestellung.tileHardcoverPrice,
      description: STRINGS.book.bestellung.tileHardcoverDescription,
      imageUrl: '/hardcover.webp',
      disabled: hardcoverUnit === undefined,
    },
    {
      key: 'layflat',
      title: STRINGS.book.bestellung.tileLayflatTitle,
      price: STRINGS.book.bestellung.tileLayflatPrice,
      description: STRINGS.book.bestellung.tileLayflatDescription,
      imageUrl: '/layflat.webp',
      disabled: layflatUnit === undefined,
    },
    {
      key: 'pappbuch',
      title: STRINGS.book.bestellung.tilePappbuchTitle,
      price: STRINGS.book.bestellung.tilePappbuchPrice,
      description: STRINGS.book.bestellung.tilePappbuchDescription,
      imageUrl: '/Buch_Pappbuch_720x480.webp',
      disabled: pappbuchUnit === undefined,
    },
  ];

  const slugByKey: Record<TileSpec['key'], string | null> = {
    hardcover: hardcoverUnit?.slug ?? null,
    layflat: layflatUnit?.slug ?? null,
    pappbuch: pappbuchUnit?.slug ?? null,
  };

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {tiles.map((t) => {
        const slug = slugByKey[t.key];
        const isSelected = slug !== null && slug === selectedSlug;
        const className = [
          TILE_BASE_CLASS,
          t.disabled ? TILE_DISABLED_CLASS : TILE_ENABLED_CLASS,
          isSelected ? TILE_SELECTED_CLASS : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              if (!t.disabled && slug !== null) onPick(slug);
            }}
            disabled={t.disabled}
            aria-pressed={isSelected}
            className={className}
          >
            <div
              className={TILE_IMAGE_SLOT_CLASS}
              aria-hidden
              style={{ backgroundImage: `url("${t.imageUrl}")` }}
            />
            <div className="flex items-baseline justify-between">
              <span className="text-base font-semibold">{t.title}</span>
              <span className="text-base font-semibold">
                {t.disabled ? STRINGS.book.bestellung.tileComingSoon : t.price}
              </span>
            </div>
            {/*
              Sebastian ruling 2026-09-25 (Webflow shipping-price
              update) — small-type shipping hint under the tile
              price. Sourced from `shippingCents` (envelope, not
              hardcoded) so a future config change flows through
              automatically. Right-aligned to sit visually under
              the price it modifies.
              Hidden on disabled tiles (Sebastian ruling 2026-10-07):
              a tile that cannot be bought shows neither price nor
              shipping hint; the right-side label becomes
              "Bald verfügbar" via the ternary above. Generic rule —
              fires for any productClass missing from
              orderableSalesUnits, not hardcover-specific.
            */}
            {!t.disabled && (
              <p className="-mt-2 text-right text-xs text-book-text-secondary">{shippingHint}</p>
            )}
            <p className="text-sm text-book-text-secondary leading-relaxed">{t.description}</p>
          </button>
        );
      })}
    </div>
  );
}
